import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import {
	type Dispatch,
	type SetStateAction,
	useState,
} from "react";
import type { DeviceDiscriminator } from "services/Devices/types";
import ButtonComponent from "../../../components/ButtonComponent";
import ModalComponent from "../../../components/ModalComponent";
import FacialControllerDeviceConfig from "../CamsPage/_components/FacialControllerConfigModal";
import IvsDeviceConfig from "../CamsPage/_components/IvsDeviceConfig";
import LprDeviceConfig from "../CamsPage/_components/LprDeviceConfig";
import RecognitionCameraConfig from "../CamsPage/_components/RecognitionCameraConfig";
import DvrDeviceConfigModal from "../DvrPage/_components/DvrDeviceConfigModal";
import DeviceLprGroupConfig from "./DeviceLprGroupConfig";
import DeviceNetworkConfig from "./DeviceNetworkConfig";
import DeviceNtpConfig from "./DeviceNtpConfig";
import DeviceRecordingScheduleConfig from "./DeviceRecordingScheduleConfig";
import DeviceRecoverEventConfig from "./DeviceRecoverEventConfig";
import DeviceVideoConfig from "./DeviceVideoConfig";

interface IDeviceConfigModalProps {
	discriminator: DeviceDiscriminator;
	isOpen: boolean;
	selectedDevice?: any;
	setSelectedDevice?: any;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const DeviceConfigModal = ({
	discriminator,
	isOpen,
	selectedDevice,
	setSelectedDevice,
	setIsOpen,
}: IDeviceConfigModalProps) => {
	const [value, setValue] = useState("1");
	const [isPendingChanges, setIsPendingChanges] = useState(false);
	const [pendingTab, setPendingTab] = useState<string | null>(null);

	const [functionToConfirmPendingChanges, setFunctionToConfirmPendingChanges] =
		useState<(val?: any) => void>(() => {});
	const [functionToSavePendingChanges, setFunctionToSavePendingChanges] =
		useState<(val?: any) => void>(() => {});

	const [
		isOpenPendingChangesConfirmModal,
		setIsOpenPendingChangesConfirmModal,
	] = useState(false);


	const handleConfig = () => {
		switch (discriminator) {
			case "ControlIdDeviceEntity":
			case "FacialControllerEntity":
				return (
					<FacialControllerDeviceConfig
						isOpen={isOpen}
						selectedDevice={selectedDevice}
						setSelectedDevice={setSelectedDevice}
						setIsOpen={setIsOpen}
					/>
				);
			case "RecognitionCameraEntity":
				return (
					<RecognitionCameraConfig
						isOpen={isOpen}
						selectedDevice={selectedDevice}
						setSelectedDevice={setSelectedDevice}
						setIsOpen={setIsOpen}
					/>
				);
			case "LprCameraEntity":
				return (
					<LprDeviceConfig
						isOpen={isOpen}
						selectedDevice={selectedDevice}
						setSelectedDevice={setSelectedDevice}
						setIsOpen={setIsOpen}
					/>
				);
			case "IvsCameraEntity":
				return (
					<IvsDeviceConfig
						isOpen={isOpen}
						selectedDevice={selectedDevice}
						setSelectedDevice={setSelectedDevice}
						setIsOpen={setIsOpen}
					/>
				);
			case "DvrEntity":
				return (
					<DvrDeviceConfigModal
						isOpen={isOpen}
						selectedDevice={selectedDevice}
						setSelectedDevice={setSelectedDevice}
						setIsOpen={setIsOpen}
					/>
				);
		}
	};

	const checkIfContainPendingChanges = () => {
		if (isPendingChanges) setIsOpenPendingChangesConfirmModal(true);
	};

	const onCloseModal = () => {
		setValue("1");
		setIsOpen(false);
		setSelectedDevice(undefined);
	};

	return (
		<ModalComponent
			onClose={() => {
				if (isPendingChanges) {
					checkIfContainPendingChanges();
					setFunctionToSavePendingChanges(() => onCloseModal);
					return;
				}
				onCloseModal();
			}}
			isOpen={isOpen}
			title={`Configurações do dispositivo ${selectedDevice?.name} ${!selectedDevice?.isOnline ? " (offline)" : ""}`}
		>
			<TabContext value={value}>
				<TabList
					variant="scrollable"
					scrollButtons="auto"
					onChange={(_event, val) => {
						if (isPendingChanges) {
							checkIfContainPendingChanges();
							setFunctionToSavePendingChanges(() => () => setValue(val));
							setPendingTab(val);
							setIsOpenPendingChangesConfirmModal(true);
						} else {
							setValue(val);
						}
					}}
				>
					<Tab label="Geral" value="1" />

					<Tab
						label={"Grupo de Placas"}
						value="2"
						sx={{
							display: discriminator === "LprCameraEntity" ? "block" : "none",
						}}
					/>
					<Tab
						label={`Rede${!selectedDevice?.isOnline ? " (offline)" : ""}`}
						value="3"
						disabled={!selectedDevice?.isOnline}
					/>
					<Tab
						label={`NTP/Horário${!selectedDevice?.isOnline ? " (offline)" : ""}`}
						value="4"
						disabled={!selectedDevice?.isOnline}
					/>
					<Tab
						label={`Vídeo${!selectedDevice?.isOnline ? " (offline)" : ""}`}
						value="5"
						disabled={!selectedDevice?.isOnline}
					/>
					<Tab
						label={`Agendamento${!selectedDevice?.isOnline ? " (offline)" : ""}`}
						value="6"
						disabled
					/>
					<Tab
						label={`Recuperação de eventos${!selectedDevice?.isOnline ? " (offline)" : ""}`}
						value="7"
						disabled={!selectedDevice?.isOnline}
					/>
				</TabList>
				<TabPanel value="1">
					{handleConfig()}
				</TabPanel>
				<TabPanel value="2">
					<DeviceLprGroupConfig
						id={selectedDevice?.id}
						setIsPendingChanges={setIsPendingChanges}
						setFunctionToConfirmPendingChanges={
							setFunctionToConfirmPendingChanges
						}
					/>
				</TabPanel>
				<TabPanel value="3">
					<DeviceNetworkConfig
						id={selectedDevice?.id}
						setIsPendingChanges={setIsPendingChanges}
						setFunctionToConfirmPendingChanges={
							setFunctionToConfirmPendingChanges
						}
					/>
				</TabPanel>
				<TabPanel value="4">
					<DeviceNtpConfig
						id={selectedDevice?.id}
						setIsPendingChanges={setIsPendingChanges}
						setFunctionToConfirmPendingChanges={
							setFunctionToConfirmPendingChanges
						}
					/>
				</TabPanel>
				<TabPanel value="5">
					<DeviceVideoConfig
						id={selectedDevice?.id}
						setIsPendingChanges={setIsPendingChanges}
						setFunctionToConfirmPendingChanges={
							setFunctionToConfirmPendingChanges
						}
					/>
				</TabPanel>
				<TabPanel value="6">
					<DeviceRecordingScheduleConfig id={selectedDevice?.id} />
				</TabPanel>
				<TabPanel value="7">
					<DeviceRecoverEventConfig id={selectedDevice?.id} />
				</TabPanel>
			</TabContext>

			<ModalComponent
				onClose={() => setIsOpenPendingChangesConfirmModal(false)}
				isOpen={isOpenPendingChangesConfirmModal}
				title={"Mudanças pendentes"}
			>
				<Typography>Tem certeza que deseja perder as alterações?</Typography>
				<Box
					display={"flex"}
					gap={3}
					justifyContent={"space-evenly"}
					marginTop={4}
				>
					<Box width={"100%"}>
						<ButtonComponent
							onClick={() => {
								functionToSavePendingChanges();
								functionToConfirmPendingChanges();
								setIsPendingChanges(false);
								if (pendingTab) {
									setValue(pendingTab);
									setPendingTab(null);
								}
								setIsOpenPendingChangesConfirmModal(false);
							}}
							fullWidth
						>
							Salvar alterações
						</ButtonComponent>
					</Box>
					<Box width={"100%"}>
						<ButtonComponent
							color={"error"}
							fullWidth
							onClick={() => {
								setIsPendingChanges(false);
								setFunctionToSavePendingChanges(() => {});
								setFunctionToConfirmPendingChanges(() => {});
								if (pendingTab) {
									setValue(pendingTab);
									setPendingTab(null);
								}
								setIsOpenPendingChangesConfirmModal(false);
							}}
						>
							Descartar alterações
						</ButtonComponent>
					</Box>
				</Box>
			</ModalComponent>
		</ModalComponent>
	);
};

export default DeviceConfigModal;
